import { RouteLocationNamedRaw } from 'vue-router'
import { SECTIONS } from '@/types/sections'
import helpers from '@/utils/helpers.ts'
import orderBy from 'lodash/orderBy'
import { sortStringToObject } from '@deepcloud/deep-ui-utils'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info.ts'
import { useDeepBoxDeepBoxesBoxesFilesTreeStore } from '@/stores/deepbox/deepboxes/boxes/files-tree.ts'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useDeepBoxSearchStore } from '@/stores/deepbox/search.ts'
import { useApi } from '@/composables/api.ts'
import { useNodeTableScroller } from '@/components/node/node-table-scroller'
import { useFilesTree } from '../files-tree'
import type { FileTree } from '@/types/file-tree.ts'
import type { Sections } from '@/types/sections.ts'
import type {
  Node,
  NodeUpdate,
  PatchNodePayload,
  SearchItem,
} from '@deepcloud/api-deep-box'

export function useNodeApi() {
  const { deepBoxAPI } = useApi()

  const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
  const deepBoxSearchStore = useDeepBoxSearchStore()

  const addNodePending = ref(false)

  async function addFolderAndRefreshTree({
    node,
    typeId,
    boxId,
    section,
  }: {
    node: Node
    typeId: string
    boxId: string
    section: Sections
  }) {
    // SET NODE
    if (deepBoxDeepBoxesBoxesNodesStore.data) {
      // Ensure nodes array exists and add the new node to the front
      deepBoxDeepBoxesBoxesNodesStore.data.nodes =
        deepBoxDeepBoxesBoxesNodesStore.data.nodes ?? []
      deepBoxDeepBoxesBoxesNodesStore.data.nodes.unshift(node)

      // Update size
      deepBoxDeepBoxesBoxesNodesStore.data.size =
        (deepBoxDeepBoxesBoxesNodesStore.data.size || 0) + 1
    }

    // Select node in nodeDataTable
    deepBoxDeepBoxesBoxesNodesStore.selectedNodeIds = [node.nodeId]

    const { refreshFilesTreeBySection } = useFilesTree()
    await refreshFilesTreeBySection(typeId, boxId, section, node.nodeId)

    const { scrollToTop } = useNodeTableScroller()
    void scrollToTop()
  }

  const saveNodePending = ref(false)

  async function refreshNodeState(nodeId: string) {
    // https://jira.abacus.ch/browse/ATK-19087 we need to get the NODE after patch it
    // because the API do not return the updated NODE
    console.warn('refreshNodeState', nodeId)
    const resNodeGet = await deepBoxAPI.value.nodesInfo.get(nodeId)
    deepBoxDeepBoxesBoxesNodesStore.updateNodeState(resNodeGet.data.node)

    // also patch the node within searchResults, if present
    if (deepBoxSearchStore.searchResults?.items) {
      const dataSetSearch = { ...deepBoxSearchStore.searchResults }
      const index = dataSetSearch.items.findIndex(
        (item) => item.nodeId === nodeId,
      )
      if (index >= 0) {
        dataSetSearch.items[index] = {
          ...dataSetSearch.items[index],
          ...resNodeGet.data.node,
          parentPath: resNodeGet.data.path,
        }
        deepBoxSearchStore.searchResults = dataSetSearch
      }
    }

    const deepBoxDeepBoxesBoxesFilesTreeStore =
      useDeepBoxDeepBoxesBoxesFilesTreeStore()

    if (deepBoxDeepBoxesBoxesFilesTreeStore.isFilesTreeOpened) {
      const node: FileTree | undefined =
        deepBoxDeepBoxesBoxesFilesTreeStore.getFileTreeNodeByNodeId(
          nodeId,
          deepBoxDeepBoxesBoxesFilesTreeStore.filesTree,
        )

      if (node) {
        // update the name only
        node.name = resNodeGet.data.node.name
        node.displayName = resNodeGet.data.node.displayName
      }
    }
  }

  async function patchNode(
    payload: { nodeId: string } & Partial<Omit<PatchNodePayload, 'nodeId'>>,
    node?: Node,
  ) {
    saveNodePending.value = true
    try {
      // Only include properties that exist in the payload
      const body: NodeUpdate = {}

      if (payload.name !== undefined) body.name = payload.name
      if (payload.hidden !== undefined) body.hidden = payload.hidden

      // Add i18n only if policy allows and payload has it
      if (node?.policy?.canI18n && payload.i18n !== undefined) {
        body.i18n = payload.i18n
      }

      const res = await deepBoxAPI.value.nodes.updateById(payload.nodeId, body)
      await refreshNodeState(payload.nodeId)
      return res
    } catch (error) {
      return Promise.reject(error)
    } finally {
      saveNodePending.value = false
    }
  }

  const deleteNodePending = ref(false)

  async function deleteNode(payload: { section: Sections; nodeId: string }) {
    deleteNodePending.value = true
    if (!payload) {
      return
    }
    const { section, nodeId } = payload

    // delete the node from state data
    if (
      deepBoxDeepBoxesBoxesNodesStore.data &&
      deepBoxDeepBoxesBoxesNodesStore.data.nodes
    ) {
      deepBoxDeepBoxesBoxesNodesStore.removeNodeState(nodeId)
    }

    // check if the delete node is within the searchResults
    if (
      deepBoxSearchStore.searchResults &&
      typeof deepBoxSearchStore.searchResults.items !== 'undefined'
    ) {
      const dataSetSearch = { ...deepBoxSearchStore.searchResults }
      const index = dataSetSearch.items.findIndex(
        (item) => item.nodeId === nodeId,
      )
      if (index >= 0) {
        // if the node was already in trash, remove it from the list
        if (section === SECTIONS.TRASH) {
          dataSetSearch.items.splice(index, 1)
          dataSetSearch.size -= 1
        } else {
          // refresh from the API: policy (canRevert, canPurge, ...) also
          // changed now that it's in trash, not just its location
          try {
            const { data } = await deepBoxAPI.value.nodesInfo.get(nodeId)
            dataSetSearch.items[index] = {
              ...dataSetSearch.items[index],
              ...data.node,
              parentPath: data.path,
            }
          } catch {
            const trashNode = deepBoxDeepBoxesBoxesNodesStore.rootNodes.trash
            if (trashNode) {
              dataSetSearch.items[index].parentPath.segments = [trashNode]
            }
          }
        }

        deepBoxSearchStore.searchResults = dataSetSearch
      }
    }

    deleteNodePending.value = false
  }

  const addFilePending = ref(false)

  function addFileFulfilled(payload: {
    section: Sections | undefined
    node: Node
    route?: RouteLocationNamedRaw
  }) {
    if (payload.route) {
      const section = helpers.getSectionFromRoute(payload.route)
      if (section && payload.section && section !== payload.section) {
        // update inbox counter if add node to inbox
        if (
          ([SECTIONS.QUEUE, SECTIONS.INBOX] as Sections[]).includes(
            payload.section,
          )
        ) {
          const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
          deepBoxBoxInfoStore.increaseBoxInfoQueueCounter()
        }
        return
      }
    }

    if (deepBoxDeepBoxesBoxesNodesStore.data) {
      // extract the current order
      const order = sortStringToObject(
        deepBoxDeepBoxesBoxesNodesStore.params?.order ?? '',
      )
      console.log('order', order)

      // unshift (not push): appending would land the node next to the
      // infinite-scroll sentinel and trigger a fetch of the next page
      // Ensure nodes array exists
      if (!deepBoxDeepBoxesBoxesNodesStore.data.nodes) {
        deepBoxDeepBoxesBoxesNodesStore.data.nodes = []
      }

      deepBoxDeepBoxesBoxesNodesStore.data.nodes.unshift(payload.node)

      // Sort nodes if order is provided
      if (order?.sortBy?.length) {
        const desc = order.sortDesc?.[0] ? 'desc' : 'asc'
        const sorter = helpers.getSorter(order)
        deepBoxDeepBoxesBoxesNodesStore.data.nodes = orderBy(
          deepBoxDeepBoxesBoxesNodesStore.data.nodes,
          sorter,
          desc,
        )
      }

      // Always put folders first
      deepBoxDeepBoxesBoxesNodesStore.data.nodes = helpers.getFoldersFirst(
        deepBoxDeepBoxesBoxesNodesStore.data.nodes,
      )

      // Update the size
      deepBoxDeepBoxesBoxesNodesStore.data.size =
        (deepBoxDeepBoxesBoxesNodesStore.data.size || 0) + 1

      // Reassign to be reactive
      deepBoxDeepBoxesBoxesNodesStore.data = {
        ...deepBoxDeepBoxesBoxesNodesStore.data,
      }
    }

    // also add it to searchResults, if present (a `parentPath` marks it as
    // one, e.g. when copied from a node already shown in search results)
    if (
      deepBoxSearchStore.searchResults?.items &&
      'parentPath' in payload.node
    ) {
      const dataSetSearch = { ...deepBoxSearchStore.searchResults }
      dataSetSearch.items = [
        payload.node as unknown as SearchItem,
        ...dataSetSearch.items,
      ]
      dataSetSearch.size += 1
      deepBoxSearchStore.searchResults = dataSetSearch
    }

    addFilePending.value = false
  }

  return {
    addFolderAndRefreshTree,
    addNodePending,
    patchNode,
    refreshNodeState,
    saveNodePending,
    deleteNode,
    deleteNodePending,
    addFileFulfilled,
    addFilePending,
  }
}
